import { useEffect, useMemo, useState } from 'react'

import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'

import { useAuthenticatedUser } from '../../../../auth/hooks/useAuthenticatedUser'
import { SelectObject } from '../../../../core/components/SelectObject'
import { SelectString } from '../../../../core/components/SelectString'
import { useProjectSelectlists } from '../../../../selectlists/hooks/useProjectSelectlists'
import type { NetbeheerderDiscipline } from '../../../../valuelists/types/NetbeheerderDiscipline'
import { useSaveEvent } from '../../hooks/useSaveEvent'
import type { HoofdleidingProject } from '../../types/HoofdleidingProject'
import {
  formatNetbeheerderDisciplineLabel,
  isNetbeheerderDisciplineEqual,
} from '../../util/disciplines-utils'

const DISCIPLINES_REDENEN = [
  'Disciplines kloppen niet',
  'Plannen aangepast, geen werkzaamheden voor deze partij',
  'Initiatiefnemer niet akkoord met offerte',
  'Partij stapt uit Combi',
]

interface Props {
  project: HoofdleidingProject
  handleClose: () => void
}

export const Disciplines = ({ project, handleClose }: Props) => {
  const { mutate: saveEvent } = useSaveEvent()
  const { deelnemer } = useAuthenticatedUser()
  const { data: selectlists } = useProjectSelectlists({
    id: project.aanvraagID,
  })
  const [reden, setReden] = useState<string | null>(null)
  const [netbeheerderDisciplines, setNetbeheerderDisciplines] = useState<
    NetbeheerderDiscipline[]
  >([])
  const [errors, setErrors] = useState<Record<string, boolean>>({})

  const handleSubmitClick = () => {
    if (!reden) {
      setErrors({
        redenError: true,
      })
    } else {
      setErrors({
        redenError: false,
      })

      saveEvent(
        {
          id: project.aanvraagID,
          data: {
            netbeheerderDisciplines,
            reden,
          },
          type: 'hoofdleiding_disciplines',
          source: 'manual',
        },
        {
          onSuccess: () => handleClose(),
        },
      )
    }
  }

  useEffect(() => {
    setNetbeheerderDisciplines(
      project.disciplineVerzamelingen as NetbeheerderDiscipline[],
    )
  }, [project.disciplineVerzamelingen])

  const netbeheerderDisciplinesOptions = useMemo(() => {
    const disciplines = selectlists.netbeheerderDisciplines ?? []

    return deelnemer
      ? disciplines.filter(
          ({ netbeheerderLabel }) => netbeheerderLabel === deelnemer,
        )
      : disciplines
  }, [selectlists.netbeheerderDisciplines, deelnemer])

  return (
    <>
      <DialogTitle>Wijzigen disciplines</DialogTitle>
      <DialogContent>
        <FormControl sx={{ width: '100%', mt: 2 }}>
          <SelectObject
            multiple
            label="Disciplines"
            options={netbeheerderDisciplinesOptions}
            getOptionLabel={formatNetbeheerderDisciplineLabel}
            isOptionEqualToValue={isNetbeheerderDisciplineEqual}
            onChange={setNetbeheerderDisciplines}
            value={netbeheerderDisciplines}
            disableClearable
          />
        </FormControl>
        <FormControl sx={{ width: '100%', mt: 2 }}>
          <SelectString
            options={DISCIPLINES_REDENEN}
            onChange={setReden}
            value={reden}
            label="Reden"
            error={errors.redenError}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus>
          Annuleren
        </Button>
        <Button color="primary" onClick={handleSubmitClick}>
          Wijzigen
        </Button>
      </DialogActions>
    </>
  )
}
